import { FormEvent, InputHTMLAttributes, useEffect, useState } from 'react'

import type { NextPage } from 'next'
import Spinner from '../components/Spinner'
import { ReasonAccount } from '../types/types'
import equal from 'fast-deep-equal'
import {
  getReasonAccountInfo,
  setReasonAccountInfo,
} from '../services/ClownApiClient'
import intersection from 'lodash.intersection'
import { KlaviyoCheckbox } from '../components/KlaviyoCheckbox'
import countries from '../data/countries.json'

// remove empty country for use in dropdown
countries.forEach((c, i) => {
  if (!c.threeLetterCode) {
    countries.splice(i, 1)
  }
})

const Profile: NextPage = () => {
  const [accountInfo, setAccountInfo] = useState<ReasonAccount>()
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('')
  const [originalAccountInfo, setOriginalAccountInfo] =
    useState<ReasonAccount>()
  const [infoChanged, setInfoChanged] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    async function fetchInfo() {
      if (!accountInfo) {
        const resp = await getReasonAccountInfo()
        setOriginalAccountInfo(resp)
        setAccountInfo(resp)
      }
    }
    fetchInfo()
  }, [accountInfo])

  useEffect(() => {
    setInfoChanged(!equal(accountInfo, originalAccountInfo))
  }, [accountInfo, originalAccountInfo])

  // In case of old dependencies: setting both autoCountryCode and addressCountryCode to same value.
  useEffect(() => {
    if (accountInfo?.autoCountryCode) {
      setAccountInfo({
        ...accountInfo,
        addressCountryCode: accountInfo.autoCountryCode,
      })
    }
  }, [accountInfo?.autoCountryCode])

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    async function updateAccountInfo() {
      if (accountInfo) {
        setErrorMessage('')
        try {
          const newInfo = await setReasonAccountInfo(
            accountInfo,
            passwordConfirmation,
          )
          setAccountInfo(newInfo)
          setOriginalAccountInfo(newInfo)
        } catch (error) {
          try {
            const errObj = JSON.parse((error as any).request.response)
            if (errObj.email) {
              setErrorMessage(errObj.email)
            } else {
              setErrorMessage(errObj.nonFieldErrors[0])
            }
          } catch (_) {
            setErrorMessage('An error occured. Please check your input.')
          }
        }
      }
      setIsLoading(false)
    }
    setIsLoading(true)
    updateAccountInfo()
  }

  function makeBasicInput(
    label: string,
    key: keyof ReasonAccount,
    attributes: InputHTMLAttributes<HTMLInputElement> = {},
  ) {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (accountInfo) {
        const value = e.target.value
        if (typeof value !== 'undefined') {
          setAccountInfo({ ...accountInfo, [key]: value })
        }
      }
    }

    if (accountInfo) {
      let value = accountInfo[key]

      if (value === null) {
        value = ''
      }

      if (typeof value !== 'string') {
        throw new Error(
          `ReasonAccount.${key} is not a string it is ${typeof value}`,
        )
      }

      return (
        <div className="row mb-20 form-group">
          <label
            className="col-12 col-sm-3 my-auto muted"
            htmlFor={`input-${key}`}>
            {label}
          </label>
          <div className="col-12 col-sm-9 my-auto">
            <input
              id={`input-${key}`}
              type="text"
              data-testid={`input-${key}`}
              className="form-control"
              value={value}
              onChange={handleChange}
              maxLength={attributes.maxLength}></input>
          </div>
        </div>
      )
    } else {
      return <></>
    }
  }

  function passwordConfirmed() {
    if (originalAccountInfo?.email === accountInfo?.email) {
      return true
    }
    return passwordConfirmation !== ''
  }

  function incompleteAdress() {
    if (
      originalAccountInfo &&
      (!originalAccountInfo.firstName ||
        !originalAccountInfo.lastName ||
        !originalAccountInfo.addressLine1 ||
        !originalAccountInfo.addressZip ||
        !originalAccountInfo.addressCity)
    ) {
      // if user is in any of these groups, address must be updated
      const groups = [
        'OnlineTrial',
        'RackExtensionTrialUser',
        'ReasonAdaptedUser',
        'ReasonEssentialsUser',
        'ReasonLimitedUser',
        'ReasonPlusUser',
        'ReasonUser',
        'ReBirthUser',
        'RecordUser',
        'ReCycleUser',
      ]
      return intersection(originalAccountInfo.groups, groups).length > 0
    }
  }

  return (
    <>
      {accountInfo && (
        <form className="mb-60" onSubmit={(e) => handleSubmit(e)}>
          <h2 className="h4">Your Reason account</h2>
          <hr />
          {incompleteAdress() && (
            <div>
              <div className="alert alert-info">
                <p className="small mb-0">
                  We&apos;ve noticed that there&apos;s no postal address
                  registered to your account. Please take a moment to update
                  your details accordingly. It&apos;s worth the time and effort,
                  because it&apos;s your postal address that we&apos;ll quiz you
                  on if you lose access to your account and are not able to use
                  the Password Help on the login page.
                </p>
              </div>
            </div>
          )}
          <div className="row mb-15">
            <label className="col-12 col-sm-3 muted">Username</label>
            <div className="col-12 col-sm-9">{accountInfo.username}</div>
          </div>
          <div className="row mb-15">
            <label className="col-12 col-sm-3 muted">First name</label>
            <div className="col-12 col-sm-9">{accountInfo.firstName}</div>
          </div>
          <div className="row mb-15">
            <label className="col-12 col-sm-3 muted">Surname</label>
            <div className="col-12 col-sm-9">{accountInfo.lastName}</div>
          </div>
          {makeBasicInput('Email', 'email', { maxLength: 80 })}
          {originalAccountInfo?.email !== accountInfo.email && (
            <div className="row mb-15">
              <>
                <label
                  className="offset-3 col-4 text-danger my-auto"
                  htmlFor="password">
                  Password required:
                </label>
                <div className="col-5">
                  <input
                    type="password"
                    className="form-control my-auto"
                    id="password"
                    name="password"
                    data-testid="input-passwordconfirmation"
                    value={passwordConfirmation}
                    onChange={(e) =>
                      setPasswordConfirmation(e.target.value)
                    }></input>
                </div>
              </>
            </div>
          )}
          {/* LG: Zipcode and state/province maxLengths are due to limitations in the `store_contact` db in the shop (populated on checkout) */}
          {makeBasicInput('Address line 1', 'addressLine1', { maxLength: 100 })}
          {makeBasicInput('Address line 2', 'addressLine2', { maxLength: 100 })}
          {makeBasicInput('Zipcode', 'addressZip', { maxLength: 30 })}
          {makeBasicInput('City', 'addressCity', { maxLength: 100 })}
          {makeBasicInput('State/Province', 'addressState', { maxLength: 30 })}
          <div className="row">
            <label className="col-12 col-sm-3 muted">Country</label>
            <div className="col-12 col-sm-9">
              <select
                className="custom-select"
                value={accountInfo.autoCountryCode}
                onChange={(e) => {
                  setAccountInfo({
                    ...accountInfo,
                    autoCountryCode: e.target.value,
                  })
                }}>
                {countries.map((country, i) => (
                  <option key={i} value={country.threeLetterCode}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row mb-15 text-muted">
            <div className="col">
              <hr />
            </div>
          </div>
          <div className="row mb-60 mt-30">
            <div className="col d-flex justify-content-center">
              {isLoading ? (
                <Spinner />
              ) : (
                <button
                  type="submit"
                  className={`btn btn-primary btn-block w-50`}
                  disabled={!infoChanged || !passwordConfirmed()}>
                  Update Information
                </button>
              )}
            </div>
          </div>
          <hr />
          <KlaviyoCheckbox></KlaviyoCheckbox>
          {errorMessage && (
            <div className="row mb-15 mt-30">
              <div className="col d-flex justify-content-center text-danger">
                {errorMessage}
              </div>
            </div>
          )}
        </form>
      )}
      {!accountInfo && <Spinner />}
    </>
  )
}

export default Profile
